/*
 * Utilities
 */

import { nanoid, customRandom, urlAlphabet } from 'nanoid';
import QueryString from 'query-string';
import Seedrandom from 'seedrandom';


const prepareHistory = (key, value, pathname) => {
    let url = window.location.href;
    let qs = QueryString.parse(window.location.search);
    if (pathname) {
        url = window.location.origin + pathname + window.location.search;
    }
    qs[key] = value;
    url = `${url.split('?')[0]}?${QueryString.stringify(qs)}`;
    return url;
};


export default {
    /**
     * Run a method safely
     * Useful for accessing a nested property of an object safely
     * e.g. util.try(() => object.property.property);
     * @param {Function} method
     * @returns {any | null} result
     */
    try: method => {
        try {
            return method();
        } catch (e) {
            return null;
        }
    },
    /**
     * Check if a value is a valid array with one or more elements
     * @param {any} array
     * @returns {boolean} result
     */
    isArray: array => {
        const tryMethod = method => {
            try {
                return method();
            } catch (e) {
                return null;
            };
        };

        if (tryMethod(() => array) && Array.isArray(array) && array.length > 0) {
            return true;
        } else {
            return false;
        }
    },
    /**
     * Convert to an array if required
     * @param {any} value
     * @returns {any[]}
     */
    toArray: value => {
        if (Array.isArray(value)) {
            return value;
        }
        if (value !== undefined) {
            return [value];
        }
        return [];
    },
    /**
     * Smooth scroll to an element
     * @param {number} duration
     * @param {object} targetElement
     * @param {Function} callback
     */
    scrollTo(duration, targetElement, callback) {
        const start = window.pageYOffset;
        const change = targetElement.getBoundingClientRect().top;
        let currentTime = 0;
        const increment = 20;

        duration = typeof duration === 'undefined' ? 500 : duration;

        const easeInOutQuad = (t, b, c, d) => {
            if ((t /= d / 2) < 1) {
                return (c / 2) * t * t + b;
            }
            return (-c / 2) * (--t * (t - 2) - 1) + b;
        };

        const animateScroll = () => {
            currentTime += increment;
            const val = easeInOutQuad(currentTime, start, change, duration);
            window.scrollTo(0, val);
            if (currentTime < duration) {
                window.requestAnimationFrame(animateScroll);
            } else if (typeof callback === 'function') {
                callback();
            }
        };

        animateScroll();
    },
    /**
     * Cookie utilities
     * src: https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie
     */
    cookies: {
        getItem: sKey => {
            if (!sKey) {
                return null;
            }
            return (
                decodeURIComponent(
                    document.cookie.replace(
                        new RegExp('(?:(?:^|.*;)\\s*' + encodeURIComponent(sKey).replace(/[\-\.\+\*]/g, '\\$&') + '\\s*\\=\\s*([^;]*).*$)|^.*$'),
                        '$1'
                    )
                ) || null
            );
        },
        setItem: (sKey, sValue, vEnd, sPath, sDomain, bSecure) => {
            if (!sKey || /^(?:expires|max\-age|path|domain|secure)$/i.test(sKey)) {
                return false;
            }
            let sExpires = '; expires=Fri, 31 Dec 9999 23:59:59 GMT';
            document.cookie =
                encodeURIComponent(sKey) +
                '=' +
                encodeURIComponent(sValue) +
                sExpires +
                (sDomain ? '; domain=' + sDomain : '') +
                (sPath ? '; path=' + sPath : '') +
                (bSecure ? '; secure' : '');
            return true;
        },
        removeItem: (sKey, sPath, sDomain) => {
            if (!this.hasItem(sKey)) {
                return false;
            }
            document.cookie =
                encodeURIComponent(sKey) +
                '=; expires=Thu, 01 Jan 1970 00:00:00 GMT' +
                (sDomain ? '; domain=' + sDomain : '') +
                (sPath ? '; path=' + sPath : '');
            return true;
        },
        hasItem: sKey => {
            if (!sKey) {
                return false;
            }
            return new RegExp('(?:^|;\\s*)' + encodeURIComponent(sKey).replace(/[\-\.\+\*]/g, '\\$&') + '\\s*\\=').test(document.cookie);
        },
        keys: () => {
            let aKeys = document.cookie.replace(/((?:^|\s*;)[^\=]+)(?=;|$)|^\s*|\s*(?:\=[^;]*)?(?:\1|$)/g, '').split(/\s*(?:\=[^;]*)?;\s*/);
            for (let nLen = aKeys.length, nIdx = 0; nIdx < nLen; nIdx++) {
                aKeys[nIdx] = decodeURIComponent(aKeys[nIdx]);
            }
            return aKeys;
        }
    },
    /**
     * Update browser history state
     * @param {string} key
     * @param {any} value
     * @param {string} pathname
     */
    updateHistoryState: (key, value, pathname) => {
        const url = prepareHistory(key, value, pathname);
        history.replaceState({}, '', url);
    },
    /**
     * Push new browser history state
     * @param {string} key
     * @param {any} value
     * @param {string} pathname
     */
    pushHistoryState: (key, value, pathname) => {
        const url = prepareHistory(key, value, pathname);
        history.pushState({}, '', url);
    },
    /**
     * Convert a number/string and currency to human readable value
     * @param {number|string} amount
     * @param {string} currency
     * @returns {string}
     */
    toCurrency: (amount, currency) => {
        // parse amount fallback
        if (typeof amount !== 'number') {
            amount = parseInt(amount);
        }
        try {
            return amount.toLocaleString(
                'en-US', {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }
            );
        } catch (err) {
            return err;
        }

    },
    /**
     * Analyse colour contrast, returns a hex value
     * @param {string} hex
     * @returns {string}
     */
    analyseContrast: hex => {
        // Return currentColor if the color is none or transparent
        if (hex === 'none' || hex === 'transparent') {
            return 'currentColor';
        }

        // If a leading # is provided, remove it
        if (hex.slice(0, 1) === '#') {
            hex = hex.slice(1);
        }
        // If a three-character hex, make six-character
        if (hex.length === 3) {
            hex = hex.split('').map(function (hex) {
                return hex + hex;
            }).join('');
        }

        // Convert to RGB value
        let r = parseInt(hex.substr(0,2),16);
        let g = parseInt(hex.substr(2,2),16);
        let b = parseInt(hex.substr(4,2),16);

        // Get YIQ ratio
        let yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

        // Check contrast, return colour
        return (yiq >= 128) ? '#2B2B2B' : '#FFFFFF';

    },
    /**
     * @function getId
     * Generate unique ID
     * @param {string} seed
     * @returns {string}
     */
    getId: (seed) => {
        if (seed) {
            return customRandom(urlAlphabet, 10, size => {
                const rng = Seedrandom(seed);
                return (new Uint8Array(size)).map(() => 256 * rng());
            })();
        }
        return nanoid();
    }
};
